/**
 * This is the component for the PortCounts in the dashboard.
 *
 * Child components: PortsHorizontalGraph.js
 *
 * Parent: Dashboard.js
 */

import React, { useState, useEffect } from "react";
import useProForma from "../../store/useProForma";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
  LabelList,
} from "recharts";
import { axisHelper } from "../../utils/graphs/axisHelper";
import { axisStyle, labelStyle } from "./styles";
import { roundToNearest } from "utils/graphs/roundNumbers";
import PortsHorizontalGraph from "./graphs/PortsHorizontalGraph";
import { colorMap } from "colors";
import CustomPortsTooltip from "./graphs/CustomPortsTooltip";

const calculateSum = (values) => {
  return Object.values(values).reduce((acc, curr) => acc + curr, 0);
};

const MAX_SITES = 10;
const OTHER_SITES_KEY = "Other sites";
const OTHER_SITES_COLOR = "darkgray";

const PortCounts = () => {
  const [incentiveSum, setIncentiveSum] = useState(0);
  const [evseCostDifference, setEvseCostDifference] = useState(0);
  let totalCount = 0;
  let max = 0;
  const {
    phases = {},
    controls = {},
    allSitesYearOverYear = {},
  } = useProForma();

  const {
    chargerIncentives = {},
    totalChargingInfrastructureCosts = {},
    totalChargingInfrastructureSavings = 0,
  } = allSitesYearOverYear;

  const phaseData = phases?.phases || [];
  const uniqueYears = [...new Set(phaseData.map((item) => item.year))];
  const uniqueSites = controls?.domiciles || [];

  const yearSiteObject = uniqueYears.reduce((acc, year) => {
    acc[year] = uniqueSites.reduce((siteAcc, site) => {
      siteAcc[site] = 0;
      return siteAcc;
    }, {});
    return acc;
  }, {});

  phaseData.forEach((phase) => {
    if (
      phase.year in yearSiteObject &&
      phase.site in yearSiteObject[phase.year]
    ) {
      let count =
        phase.port_10_20_kw +
        phase.port_25_kw +
        phase.port_180_200_kw +
        phase.port_less_than_10_kw;
      yearSiteObject[phase.year][phase.site] += count;
      totalCount += count;
    }
  });

  // Initialize per-site totals for sorting and aggregation.
  const siteTotals = uniqueSites.reduce((acc, site) => {
    acc[site] = 0;
    return acc;
  }, {});

  Object.values(yearSiteObject).forEach((sites) => {
    Object.entries(sites).forEach(([site, count]) => {
      if (site in siteTotals) {
        siteTotals[site] += count;
      }
    });
  });

  const sortedSites = [...uniqueSites].sort(
    (a, b) => (siteTotals[b] || 0) - (siteTotals[a] || 0),
  );

  const hasOtherSites = sortedSites.length > MAX_SITES;
  const visibleSites = hasOtherSites
    ? sortedSites.slice(0, MAX_SITES - 1)
    : sortedSites;
  const otherSites = hasOtherSites ? sortedSites.slice(MAX_SITES - 1) : [];
  const chartSites = hasOtherSites
    ? [...visibleSites, OTHER_SITES_KEY]
    : visibleSites;

  const data = Object.entries(yearSiteObject).map(([year, sites]) => {
    const entry = { year, total: 0 };
    visibleSites.forEach((site) => {
      const count = sites[site] || 0;
      entry[site] = count;
      entry.total += count;
    });
    if (hasOtherSites) {
      const otherCount = otherSites.reduce(
        (sum, site) => sum + (sites[site] || 0),
        0,
      );
      entry[OTHER_SITES_KEY] = otherCount;
      entry.total += otherCount;
    }
    return entry;
  });

  data.forEach((entry) => {
    let tempCount = 0;
    chartSites.forEach((site) => {
      tempCount += entry[site];
    });
    if (tempCount > max) {
      max = tempCount;
    }
  });

  useEffect(() => {
    calculateSum(chargerIncentives);
    setIncentiveSum(calculateSum(chargerIncentives));
    setEvseCostDifference(
      calculateSum(totalChargingInfrastructureCosts) -
        calculateSum(totalChargingInfrastructureSavings),
    );
  }, [
    chargerIncentives,
    totalChargingInfrastructureCosts,
    totalChargingInfrastructureSavings,
  ]);

  return (
    <div className="bg-white rounded-2xl p-4 flex flex-col gap-6 border">
      <div>
        <div className="flex gap-2 text-[#1b1c1b] text-xl font-medium font-['Inter'] leading-normal">
          <img
            src="icons/portsicon.svg"
            width="20"></img>
          Ports
        </div>
      </div>
      <div className="mt-0 flex flex-col-reverse gap-4">
        <div className="flex justify-start gap-6">
          <div>
            <div className="flex items-end">
              <div className="text-[#1b1c1b] text-[29px] font-semibold font-['Inter'] leading-[34.80px]">
                {totalCount}
              </div>
              <div className="text-[#1b1c1b] text-xl font-medium font-['Inter'] leading-normal">
                Ports
              </div>
            </div>
            <div className="text-[#7e817d] text-[13px] font-normal font-['Roboto'] leading-none">
              Total Ports to be Installed
            </div>
          </div>
        </div>
        <div className="flex flex-1 justify-between gap-6">
          <div className="w-1/2">
            <div
              className={`flex items-end ${
                Math.round(incentiveSum) === 0
                  ? "text-[#1b1c1b]"
                  : Math.round(incentiveSum) < 0
                    ? "text-[#1b1c1b]"
                    : "text-[#008000]"
              }`}>
              <div className="text-xl font-medium font-['Inter'] leading-normal">
                $
              </div>
              <div className="text-[29px] font-semibold font-['Inter'] leading-[34.80px]">
                {roundToNearest(incentiveSum).val}
              </div>
              <div className="text-xl font-medium font-['Inter'] leading-normal">
                {roundToNearest(incentiveSum).suffix}
              </div>
            </div>
            <div className="text-[#7e817d] text-[13px] font-normal font-['Roboto'] leading-none">
              Total Charger Incentive Value
            </div>
          </div>
          <div className="w-1/2">
            <div className={`flex items-end text-[#1b1c1b]`}>
              <div className="text-xl font-medium font-['Inter'] leading-normal">
                $
              </div>
              <div className="text-[29px] font-semibold font-['Inter'] leading-[34.80px]">
                {roundToNearest(evseCostDifference).val}
              </div>
              <div className="text-xl font-medium font-['Inter'] leading-normal">
                {roundToNearest(evseCostDifference).suffix}
              </div>
            </div>
            <div className="text-[#7e817d] text-[13px] font-normal font-['Roboto'] leading-none">
              Total EVSE Costs
            </div>
          </div>
        </div>
      </div>
      <PortsHorizontalGraph />

      <ResponsiveContainer
        width="100%"
        height={300}>
        <BarChart
          width={500}
          height={300}
          data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="year"
            style={axisStyle}
            stroke="#7E817D"
          />
          <YAxis
            stroke="#7E817D"
            style={axisStyle}
            domain={[0, Math.ceil((max * 1.1) / 4) * 4]}
            ticks={axisHelper(0, Math.ceil((max * 1.1) / 4) * 4, 5)}>
            <Label
              value="Ports"
              angle={-90}
              offset={30}
              position="insideLeft"
              style={labelStyle}
            />
          </YAxis>
          <Tooltip content={<CustomPortsTooltip />} />
          <Legend
            align="right"
            wrapperStyle={{ fontSize: "12px" }}
          />
          {chartSites.map((site, index) => (
            <Bar
              radius={[5, 5, 5, 5]}
              key={site}
              dataKey={site}
              stackId="a"
              fill={
                site === OTHER_SITES_KEY
                  ? OTHER_SITES_COLOR
                  : `${colorMap[index]}`
              }>
              {index === chartSites.length - 1 && (
                <LabelList
                  style={{
                    textAlign: "center",
                    color: "#40423f",
                    fontSize: "13px",
                    fontWeight: "normal",
                    fontFamily: "Roboto",
                    lineHeight: "1",
                  }}
                  dataKey="total"
                  position="top"
                />
              )}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PortCounts;
